import {
  attemptStartProductAnalyticsLogging as attemptStartProductAnalyticsLoggingAction,
  updateSessionAfterLogin
} from "actions";
import { fetchOne } from "actions/resources";
import Alerts from "components/Common/Alerts";
import Loading from "components/Common/Loading";
import Modal from "components/Common/Modal";
import SessionTimeoutModal from "components/Common/ModalSessionTimeout";
import Navigator from "components/Common/Navigator";
import Splash from "components/Common/Splash";
import WindowAlert from "components/Common/WindowAlert";
import ErrorPage from "components/Pages/ErrorPage";
import Login from "components/Pages/Login";
import PasswordSet from "components/Pages/PasswordSet";
import { ConnectedRouter } from "connected-react-router";
import * as PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { Redirect, Route, Switch } from "react-router";
import { Client, User } from "resourceModels";
import { getResource, getResourcesRequest } from "selectors/resources";
import { trackReactComponentError } from "services/errorTracker";
import RequestState from "services/requestState";
import { history } from "services/store";
import adaEmbed from "services/ada-embed";
import LazyRoute from "./Common/LazyRoute";

import "./root.scss";

const AnalyticsPage = React.lazy(() => import("components/Pages/AnalyticsPage"));
const Conversations = React.lazy(() => import("components/Pages/Conversations"));
const Questions = React.lazy(() => import("components/Pages/Questions"));
const Responses = React.lazy(() => import("components/Pages/Responses"));
const Settings = React.lazy(() => import("components/Pages/Settings"));
const ErrorDashboard = React.lazy(() => import("components/Pages/ErrorDashboard"));


class Root extends React.Component {
  /**
   * Hooks
   */

  /**
   */
  componentDidMount() {
    const {
      /** @type {Client} */
      client,
      /** @type {User} */
      user,
      fetchClient,
      fetchUser,
      updateSession
    } = this.props;

    if (!client) {
      fetchClient();
    }

    const currentPath = String(window.location.pathname);
    const onPasswordSetPage = currentPath.includes("/users/password/set/");

    // If a 403 (forbidden) is returned, this is handled in the middleware, user will be sent to "/"
    if (!user && !onPasswordSetPage) {
      fetchUser().then(
        (response) => {
          updateSession(response.data.user);
        }
      ).catch((response) => {
        if (response.status === 401 || response.status === 403 || response.status === 419) {
          // Do nothing since we expect 401s/403s here, specifically when the user is not logged in
          // 419s also occur if the user directly goes to the login page after the session times out
        } else {
          throw response;
        }
      });
    }
  }

  /**
   * @param {Object} prevProps
   */
  componentDidUpdate(prevProps) {
    const {
      /** @type {Client} */
      client,
      user,
      attemptStartProductAnalyticsLogging
    } = this.props;

    if (client && user) {
      attemptStartProductAnalyticsLogging();
    }

    if (!prevProps.client && client && client.features.get("embedTestBot")) {
      adaEmbed.start();
    }
  }

  /**
   * @param {Error} error
   * @param {React.ErrorInfo} info
   */
  componentDidCatch(error, info) {
    trackReactComponentError(error, info);
  }

  /**
   * Methods
   */

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      /** @type {Client} */
      client,
      error,
      navigator,
      location,
      /** @type {User} */
      user,
      /** @type {RequestState} */
      clientRequest,
      /** @type {RequestState} */
      userRequest,
      sessionIsAuthenticated,
      sessionIsAuthenticating
    } = this.props;

    if (!client && clientRequest) {
      if (clientRequest.isPending) {
        return <Loading />;
      }

      if (clientRequest.isFailed) {
        return <ErrorPage />;
      }
    }

    if (
      !user &&
      userRequest &&
      userRequest.isPending &&
      !sessionIsAuthenticating
    ) {
      return <Loading />;
    }

    let view;

    if (error.get("pageError")) {
      view = <ErrorPage />;
    } else if (!sessionIsAuthenticated) {
      view = (
        <Switch>
          <Route path="/users/password/set/:token" component={PasswordSet} />
          <Route path="/" component={Login} />
        </Switch>
      );
    } else {
      view = (
        <div className="app-container" id="app-container">
          <Route
            path="(/answers|/conversations|/questions|/analytics|/settings|/error-dashboard)"
            render={
              props => (
                <Navigator
                  {...props}
                  navigator={navigator}
                  location={location}
                />
              )
            }
          />
          <Switch>
            <Route path="/users/password/set/:token" component={PasswordSet} />
            <LazyRoute path="/answers/:id" component={Responses} />
            <LazyRoute path="/answers" component={Responses} />
            <Redirect exact from="/" to="/answers" />
            <LazyRoute path="/conversations/:id" component={Conversations} />
            <LazyRoute path="/conversations" component={Conversations} />
            <LazyRoute path="/questions" component={Questions} />
            <LazyRoute path="/analytics" component={AnalyticsPage} />
            <LazyRoute path="/settings" component={Settings} />
            <LazyRoute path="/error-dashboard" component={ErrorDashboard} />
            <Redirect from="/app" to="/answers" />
            <Redirect from="/responses" to="/answers" />
            <Route path="*" render={() => <Splash title="404" description="The page you are looking for doesn't exist" />} />
          </Switch>
        </div>
      );
    }

    return (
      <div className="Root">
        <ConnectedRouter history={history}>
          <>
            {view}
            <Alerts />
            <WindowAlert />
            <Modal />
            <SessionTimeoutModal />
          </>
        </ConnectedRouter>
      </div>
    );
  }
}

Root.propTypes = {
  client: PropTypes.instanceOf(Client),
  user: PropTypes.instanceOf(User),
  clientRequest: PropTypes.instanceOf(RequestState),
  userRequest: PropTypes.instanceOf(RequestState),
  sessionIsAuthenticating: PropTypes.bool.isRequired,
  sessionIsAuthenticated: PropTypes.bool.isRequired,
  fetchClient: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  updateSession: PropTypes.func.isRequired,
  attemptStartProductAnalyticsLogging: PropTypes.func.isRequired
};

Root.defaultProps = {
  client: null,
  user: null,
  clientRequest: null,
  userRequest: null
};

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapState(state) {
  const {
    session, error
  } = state;

  return {
    client: getResource(state, "client"),
    user: getResource(state, "user"),
    clientRequest: getResourcesRequest(state, "client", "default"),
    userRequest: getResourcesRequest(state, "user", "default"),
    sessionIsAuthenticating: session.isAuthenticating,
    sessionIsAuthenticated: session.isAuthenticated,
    error
  };
}

/**
 * @param {Function} dispatch
 * @returns {Object}
 */
function mapDispatchToProps(dispatch) {
  return {
    fetchClient: () => dispatch(fetchOne("client")),
    fetchUser: () => dispatch(fetchOne("user")),
    updateSession: () => dispatch(updateSessionAfterLogin()),
    attemptStartProductAnalyticsLogging: () => dispatch(attemptStartProductAnalyticsLoggingAction())
  };
}

const Connector = connect(mapState, mapDispatchToProps)(Root);

export default Connector;
